/**
 * Product Category Trend Research Tool
 *
 * Allows super admin to research product-specific trends for selected categories.
 * Categories are sorted by importance (product count).
 *
 * Date: 2025-11-16
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Package,
  Sparkles,
  ArrowLeft,
  Play,
  XCircle,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface ProductCategory {
  category: string;
  productCount: number;
}

interface ResearchProgress {
  status: 'idle' | 'researching' | 'completed' | 'error';
  categoriesProcessed: number;
  totalCategories: number;
  trendsResearched: number;
  trendsStored: number;
  failures: { category: string; error: string }[];
  currentCategory?: string;
}

export default function TrendResearchTool() {
  const { sectorId } = useParams<{ sectorId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sectorName, setSectorName] = useState<string>('');
  const [progress, setProgress] = useState<ResearchProgress>({
    status: 'idle',
    categoriesProcessed: 0,
    totalCategories: 0,
    trendsResearched: 0,
    trendsStored: 0,
    failures: [],
  });

  useEffect(() => {
    loadData();
  }, [sectorId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load sector details
      const sectorResponse = await authFetch(`/api/sectors/${sectorId}`);
      const sectorData = await sectorResponse.json();
      setSectorName(sectorData.sector?.name || 'Unknown Sector');

      // Load product categories
      const categoriesResponse = await authFetch(
        `/api/sectors/${sectorId}/product-categories?limit=100`
      );
      const categoriesData = await categoriesResponse.json();

      if (!categoriesData.success) {
        throw new Error(categoriesData.error || 'Failed to load product categories');
      }

      setCategories(categoriesData.categories || []);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load product categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(c => c.category));
    }
  };

  const handleToggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleStartResearch = async () => {
    if (selectedCategories.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one category',
        variant: 'destructive',
      });
      return;
    }

    try {
      setProgress({
        status: 'researching',
        categoriesProcessed: 0,
        totalCategories: selectedCategories.length,
        trendsResearched: 0,
        trendsStored: 0,
        failures: [],
      });

      const response = await authFetch(
        `/api/sectors/${sectorId}/research-category-trends`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            limit: selectedCategories.length,
            categories: selectedCategories,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to research trends');
      }

      setProgress({
        status: 'completed',
        categoriesProcessed: data.categoriesProcessed,
        totalCategories: selectedCategories.length,
        trendsResearched: data.trendsResearched,
        trendsStored: data.trendsStored,
        failures: data.failures || [],
      });

      toast({
        title: 'Research Completed',
        description: `Researched ${data.trendsResearched} trends, stored ${data.trendsStored}`,
      });
    } catch (error: any) {
      console.error('Error researching trends:', error);
      setProgress(prev => ({
        ...prev,
        status: 'error',
      }));
      toast({
        title: 'Error',
        description: error.message || 'Failed to research trends',
        variant: 'destructive',
      });
    }
  };

  const getSimplifiedCategoryName = (category: string) => {
    return category.includes('>') ? category.split('>').pop()?.trim() || category : category;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/sectors`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Product Trend Research</h1>
              <p className="text-muted-foreground mt-2">
                Research product-specific trends for {sectorName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          Select product categories to research trends. The AI will identify product-specific innovations,
          technology advances, and market developments (NOT generic industry trends like "AI" or "sustainability").
        </AlertDescription>
      </Alert>

      {/* Progress */}
      {progress.status !== 'idle' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {progress.status === 'researching' && (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Researching Trends...
                </>
              )}
              {progress.status === 'completed' && (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Research Completed
                </>
              )}
              {progress.status === 'error' && (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  Research Failed
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {progress.categoriesProcessed} / {progress.totalCategories}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Trends Researched</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {progress.trendsResearched}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Trends Stored</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {progress.trendsStored}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Failures</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {progress.failures.length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {progress.failures.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">Failed Categories:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {progress.failures.map((failure, idx) => (
                      <li key={idx} className="text-sm">
                        {failure.category}: {failure.error}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Select Product Categories
              </CardTitle>
              <CardDescription>
                Categories sorted by importance ({categories.length} total, {selectedCategories.length} selected)
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedCategories.length === categories.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No product categories found for this sector
              </AlertDescription>
            </Alert>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedCategories.length === categories.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Products</TableHead>
                    <TableHead className="text-right">Importance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat, idx) => (
                    <TableRow
                      key={idx}
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => handleToggleCategory(cat.category)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedCategories.includes(cat.category)}
                          onCheckedChange={() => handleToggleCategory(cat.category)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {getSimplifiedCategoryName(cat.category)}
                          </div>
                          {cat.category.includes('>') && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {cat.category}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">
                          {cat.productCount}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={
                            idx < 5 ? 'default' :
                            idx < 20 ? 'secondary' :
                            'outline'
                          }
                        >
                          {idx < 5 ? 'High' : idx < 20 ? 'Medium' : 'Low'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Button */}
      <Card>
        <CardHeader>
          <CardTitle>Start Research</CardTitle>
          <CardDescription>
            This will research product-specific trends for {selectedCategories.length} selected categories
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedCategories.length > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <strong>{selectedCategories.length} categories selected</strong>
                <br />
                Estimated time: ~{selectedCategories.length * 3} seconds
                <br />
                Expected trends: ~{selectedCategories.length * 7} product-specific trends
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => navigate(`/sectors`)}
              disabled={progress.status === 'researching'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartResearch}
              disabled={
                progress.status === 'researching' ||
                selectedCategories.length === 0
              }
            >
              {progress.status === 'researching' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Researching... ({progress.categoriesProcessed}/{progress.totalCategories})
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Start Research
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What happens during research?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>For each selected category, the AI will:</p>
          <ol className="list-decimal list-inside space-y-1 ml-4">
            <li>Research latest innovations and technology advances specific to that product type</li>
            <li>Identify material improvements, design changes, and new features</li>
            <li>Find consumer preference shifts and regulatory changes</li>
            <li>Generate 5-10 product-specific trends (NOT generic industry trends)</li>
            <li>Store trends in the database for use in PC-TR question generation</li>
          </ol>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <strong className="text-foreground">Example Output:</strong>
            <ul className="mt-2 space-y-1">
              <li className="text-green-600">✅ "Tubeless tire technology adoption"</li>
              <li className="text-green-600">✅ "Wider tire profiles (28mm+) becoming standard"</li>
              <li className="text-red-600">❌ "Digital transformation in cycling" (too generic)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
